/**
 * TimelineFilterSortControls Component
 *
 * Sort field selector and direction toggle button.
 */

import React, { useCallback } from "react";
import { Button } from "@/components/ui/button";
import { ResponsiveCommandSelect } from "@/components/ui/responsive-select";
import { SortAsc, SortDesc } from "lucide-react";
import { SORT_OPTIONS } from "../types/timeline-filters";
import type { TimelineSortOptions } from "../types/timeline-filters";

interface TimelineFilterSortControlsProps {
  sort: TimelineSortOptions;
  onSortFieldChange: (field: string) => void;
  onSortDirectionToggle: () => void;
}

const TimelineFilterSortControlsBase: React.FC<
  TimelineFilterSortControlsProps
> = ({ sort, onSortFieldChange, onSortDirectionToggle }) => {
  const handleFieldChange = useCallback(
    (value: string) => {
      onSortFieldChange(value);
    },
    [onSortFieldChange]
  );

  return (
    <div className="flex items-center gap-2 bg-card rounded-md border p-1">
      <ResponsiveCommandSelect
        value={sort.field}
        onValueChange={handleFieldChange}
        options={SORT_OPTIONS.map((option) => ({
          value: option.value,
          label: option.label,
        }))}
        placeholder="Sort by..."
        title="Sort Options"
        description="Choose how to sort timeline items"
      />

      <Button
        variant="ghost"
        size="sm"
        onClick={onSortDirectionToggle}
        className="h-8 w-8 p-0 hover:bg-card-100"
        title={`Sort ${sort.direction === "asc" ? "ascending" : "descending"}`}
      >
        {sort.direction === "asc" ? (
          <SortAsc className="h-4 w-4" />
        ) : (
          <SortDesc className="h-4 w-4" />
        )}
      </Button>
    </div>
  );
};

export const TimelineFilterSortControls = React.memo(
  TimelineFilterSortControlsBase
);
